// frontend/src/modules/monitoring/components/DailyActivityBars.tsx
//
// Per-day activity bars in the Pearl + Coral chart vocabulary (see
// design-system/charts.tsx): rounded pill bars, coral = active captures,
// neutral ink/white = idle captures, dark-mode aware. Bar height scales with
// total captures; the coral segment is the active share of that day.

import * as React from 'react';

export interface DailyActivityDatum {
  /** ISO date (YYYY-MM-DD). */
  date: string;
  total_captures: number;
  active_captures: number;
  idle_captures: number;
  activity_ratio: number;
}

function dayLabel(isoDate: string): string {
  return isoDate.slice(8, 10); // day of month
}

export function DailyActivityBars({
  data,
  height = 160,
  className,
}: {
  data: DailyActivityDatum[];
  height?: number;
  className?: string;
}) {
  const max = Math.max(1, ...data.map((d) => d.total_captures));
  // For long ranges, only label every Nth bar so the axis stays legible.
  const labelEvery = data.length > 45 ? 7 : data.length > 14 ? 2 : 1;

  return (
    <div className={className}>
      <div className="flex items-end gap-1" style={{ height }}>
        {data.map((d, i) => {
          const totalPct = Math.max(3, (d.total_captures / max) * 100);
          const activePct = d.total_captures > 0 ? (d.active_captures / d.total_captures) * 100 : 0;
          return (
            <div
              key={d.date}
              className="flex-1 flex flex-col items-center justify-end h-full min-w-[6px] group relative"
            >
              {/* Hover readout */}
              <div className="pointer-events-none absolute -top-1 z-10 hidden group-hover:block bg-ink dark:bg-white text-white dark:text-ink rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap">
                {d.date} · {d.total_captures} captures · {Math.round(d.activity_ratio * 100)}% active
              </div>
              <div
                className="w-full max-w-[18px] rounded-full overflow-hidden flex flex-col-reverse bg-ink/12 dark:bg-white/12"
                style={{ height: `${totalPct}%` }}
                aria-label={`${d.date}: ${d.total_captures} captures, ${Math.round(d.activity_ratio * 100)}% active`}
              >
                <div className="w-full bg-coral" style={{ height: `${activePct}%` }} />
              </div>
              <span className="mt-2 text-[10px] text-ink/55 dark:text-white/55 font-medium tabular-nums">
                {i % labelEvery === 0 ? dayLabel(d.date) : ' '}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3">
        <LegendSwatch className="bg-coral" label="Active captures" />
        <LegendSwatch className="bg-ink/12 dark:bg-white/12" label="Idle captures" />
      </div>
    </div>
  );
}

function LegendSwatch({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${className}`} />
      {label}
    </span>
  );
}
